/**
 * WAF (Owl / Mlytics) — shared data-layer infra for the service-scoped
 * `/api/v3/services/:service_id/waf/*` module on @mgmt (serviceApiV3). Unlike
 * the CDN per-domain endpoints (`/cdn/domains/:d/*`, vendor fan-out map), this
 * module is a flat pass-through to the Owl provider and returns the unified v3
 * envelope:
 *   - object: `{ status, data }`
 *   - list:   `{ status, data: [...], pagination }`
 *   - error:  `{ status, message, error? }`
 *
 * The service id lives in the path (`/services/:service_id/waf/…`); domain-
 * anchored endpoints also take `?domain=`. The provider resolves the customer
 * (tier-2 Mlytics impersonation) from the service's zone.
 */
import type { AxiosResponse } from 'axios';
import { serviceApiV3 } from '@/api/client';

export { serviceApiV3 };

// ---- Envelope ------------------------------------------------------------

export interface Envelope<T> {
  status?: number;
  data: T;
  message?: string;
}

/** Page-based pagination metadata (BE `providers.PageInfo`). */
export interface PageInfo {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface ListEnvelope<T> {
  status?: number;
  data: T[];
  pagination?: PageInfo;
  message?: string;
}

export interface ListResult<T> {
  items: T[];
  pagination?: PageInfo;
}

/** Inner `data` of an object response. */
export function unwrap<T>(res: AxiosResponse<Envelope<T>>): T {
  return res.data?.data as T;
}

/** `{ items, pagination }` from a list response. */
export function unwrapList<T>(res: AxiosResponse<ListEnvelope<T>>): ListResult<T> {
  return { items: res.data?.data ?? [], pagination: res.data?.pagination };
}

/**
 * Human-readable error message from a WAF API error. The module emits
 * `{ status, message, error, detail? }`; `detail` carries the vendor's specific
 * reason for caller-fixable 4xx errors. Fall back through axios message then
 * `fallback`.
 */
export function wafExtractError(e: unknown, fallback = 'Request failed'): string {
  if (e && typeof e === 'object') {
    const ax = e as {
      response?: { data?: { message?: string; error?: string; detail?: string } };
      message?: string;
    };
    const data = ax.response?.data;
    // The BE surfaces the vendor's specific reason in `detail` for caller-fixable
    // 4xx errors (e.g. "duplicated zone", "Schema check fail: …") alongside a
    // friendly `message`. Prefer the two together so the toast says what actually
    // went wrong, not just the generic headline.
    const detail = data?.detail?.trim();
    const beMsg = (data?.message ?? data?.error)?.trim();
    if (detail && beMsg && detail !== beMsg) return `${beMsg}: ${detail}`;
    if (detail) return detail;
    if (beMsg) return beMsg;
    if (ax.message) return ax.message;
  }
  return fallback;
}

/** Stable query-string fragment for query keys (sorted, empties dropped). */
export function toQuery(params?: Record<string, unknown>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const k of Object.keys(params).sort()) {
    const v = params[k];
    if (v === undefined || v === null || v === '') continue;
    sp.append(k, String(v));
  }
  return sp.toString();
}

// ---- Domain types (json mirrors BE `providers.*` in provider_waf.go) ------

/** Upstream/origin configuration of a domain. */
export interface DomainOrigin {
  type?: string; // IPv4 | ...
  sources: string[];
  ssl_policies?: string;
  sni: boolean;
  port_http?: number;
  port_https?: number;
  balance_mode?: string;
  keepalive_timeout?: number; // detail only
}

/** One CDN platform attached to a domain. The tail fields are detail-only. */
export interface DomainPlatform {
  platform_id: string;
  name: string; // e.g. "vncdn"
  display_name: string; // e.g. "VNCDN"
  status: string;
  type?: string;
  enable: boolean;
  ssl: boolean;
  created_at?: number;
  updated_at?: number;
  // Detail-only
  addon_id?: string;
  edge_domain?: string;
  identifier?: string;
  ssl_verification?: string;
  websocket?: boolean;
  websocket_status?: string;
  termination?: boolean;
  support_custom_ssl?: boolean;
  need_approval?: boolean;
  usable?: boolean;
  deletable?: boolean;
}

/** One CDN domain (Mlytics "zone") — list shape. */
export interface WafDomain {
  domain: string;
  cname: string;
  organization_id?: string;
  subscription_id?: string;
  plan_id?: string;
  status: string;
  type?: string; // e.g. "main"
  region?: string;
  enable: boolean;
  locked: boolean;
  waf_enabled: boolean;
  waf_status?: string;
  dns_status?: string;
  ssl_enabled: boolean;
  ssl_type?: string;
  ssl_redirect: boolean;
  auto_compression: boolean;
  tags: string[];
  origin: DomainOrigin;
  platforms: DomainPlatform[];
  created_at?: number;
  updated_at?: number;
}

export interface DomainSSL {
  enable: boolean;
  type?: string; // e.g. "zero_ssl"
  redirect: boolean;
  cert_id?: number;
  setup: boolean;
}

export interface DomainRegion {
  region?: string; // e.g. "VN"
  region_type?: string; // e.g. "MLY"
  override_enable: boolean;
}

export interface DomainCustomPage {
  block: number;
  error_5xx: number;
  rate_limit: number;
  js: number;
  captcha: number;
  maint_enable: boolean;
  maint_page_id?: number;
}

export interface DomainCORS {
  enable: boolean;
  origins: string[];
  allow_methods: string[];
}

export interface DomainHTTPVersion {
  version?: string; // e.g. "1.1"
  enable_http2: boolean;
}

export interface DomainICP {
  installed: boolean;
  enable: boolean;
  status?: string;
  last_verify_time?: number;
}

/** One selectable CDN platform (catalog — `GET /services/:service_id/waf/platforms`). */
export interface WafPlatform {
  id: string;
  name: string; // slug, e.g. "vncdn"
  display_name: string; // human label, e.g. "VNCDN"
  category_id?: number;
}

/** One domain tag (catalog — `GET /services/:service_id/waf/tags`). */
export interface WafTag {
  id: number;
  name: string;
  domains_count: number;
  created_at?: string;
  updated_at?: string;
}

/** Full configuration of a single domain (`GET /services/:service_id/waf/domains/detail`). */
export interface WafDomainDetail {
  domain: string;
  cname: string;
  organization_id?: string;
  subscription_id?: string;
  plan_id?: string;
  status: string;
  internal_status?: string;
  type?: string;
  enable: boolean;
  locked: boolean;
  terminated: boolean;
  mega: boolean;
  deletable: boolean;
  tags: string[];
  global_fallback?: string;

  waf_enabled: boolean;
  waf_status?: string;
  dns_status?: string;

  origin: DomainOrigin;
  platforms: DomainPlatform[];
  ssl: DomainSSL;
  region: DomainRegion;
  custom_page: DomainCustomPage;
  cors: DomainCORS;
  http_version: DomainHTTPVersion;
  icp: DomainICP;

  upstream_host?: string;
  auto_compression: boolean;
  compress_type?: string;
  ignore_query_string: boolean;
  websocket_enable: boolean;
  early_hint_enable: boolean;
  ocsp_enable: boolean;

  created_at?: number;
  updated_at?: number;
}

// ---- Query keys ----------------------------------------------------------

export const wafKeys = {
  all: ['waf'] as const,
  service: (serviceId: string) => ['waf', serviceId, 'service'] as const,
  domains: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'domains', toQuery(q)] as const,
  domain: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'domain', domain] as const,
  platforms: (serviceId: string) => ['waf', serviceId, 'platforms'] as const,
  platformQuery: (serviceId: string, domains: string[], q?: Record<string, unknown>) =>
    ['waf', serviceId, 'platforms-query', domains, toQuery(q)] as const,
  powerUpCategories: (serviceId: string) => ['waf', serviceId, 'powerup-categories'] as const,
  selectablePlatforms: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'platforms-selectable', domain] as const,
  ruleOptions: (serviceId: string) => ['waf', serviceId, 'rule-options'] as const,
  rules: (serviceId: string, domain: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'rules', domain, toQuery(q)] as const,
  accessControl: (
    serviceId: string,
    domain: string,
    kind: string,
    list: string,
    q?: Record<string, unknown>,
  ) => ['waf', serviceId, 'access-control', domain, kind, list, toQuery(q)] as const,
  domainPlatforms: (serviceId: string, domain: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'domain-platforms', domain, toQuery(q)] as const,
  ddos: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'ddos', domain] as const,
  botCatalog: (serviceId: string, category: string) =>
    ['waf', serviceId, 'bot-catalog', category] as const,
  atpRules: (serviceId: string, domain: string, category: string) =>
    ['waf', serviceId, 'atp-rules', domain, category] as const,
  atpTemplates: (serviceId: string, category: string) =>
    ['waf', serviceId, 'atp-templates', category] as const,
  firewallRules: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'firewall-rules', domain] as const,
  firewallSchema: (serviceId: string) => ['waf', serviceId, 'firewall-schema'] as const,
  originRules: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'origin-rules', domain] as const,
  originSchema: (serviceId: string) => ['waf', serviceId, 'origin-schema'] as const,
  edgeRules: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'edge-rules', domain] as const,
  edgeTemplates: (serviceId: string) => ['waf', serviceId, 'edge-templates'] as const,
  edgeReplaceHeaders: (serviceId: string) => ['waf', serviceId, 'edge-replace-headers'] as const,
  edgeDynamicValues: (serviceId: string) => ['waf', serviceId, 'edge-dynamic-values'] as const,
  apiProtectionRules: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'api-protection-rules', domain] as const,
  tags: (serviceId: string) => ['waf', serviceId, 'tags'] as const,
  ssl: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'ssl', toQuery(q)] as const,
  sslOptions: (serviceId: string) => ['waf', serviceId, 'ssl-options'] as const,
  sslVerification: (serviceId: string, certId: number) =>
    ['waf', serviceId, 'ssl', certId, 'verification'] as const,
  customPages: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'custom-pages', toQuery(q)] as const,
  dnsZones: (serviceId: string) => ['waf', serviceId, 'dns-zones'] as const,
  dnsZoneDomains: (serviceId: string, zoneId: string) =>
    ['waf', serviceId, 'dns-zone-domains', zoneId] as const,
  dnsRecords: (serviceId: string, zoneId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'dns-records', zoneId, toQuery(q)] as const,
  dnsRecordTypes: (serviceId: string) => ['waf', serviceId, 'dns-rrtypes'] as const,
  dnsNameservers: (serviceId: string) => ['waf', serviceId, 'dns-nameservers'] as const,
  dnsNsStatus: (serviceId: string, zoneId: string) =>
    ['waf', serviceId, 'dns-ns-status', zoneId] as const,
  dnsZoneReview: (serviceId: string, zoneId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'dns-zone-review', zoneId, toQuery(q)] as const,
  cacheRules: (serviceId: string, domain: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'cache-rules', domain, toQuery(q)] as const,
  cacheOptions: (serviceId: string) => ['waf', serviceId, 'cache-options'] as const,
  cachePurges: (serviceId: string, domain: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'cache-purges', domain, toQuery(q)] as const,
  dnsQueryDiagram: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'dns-query-diagram', toQuery(q)] as const,
  edgeQueryDiagram: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'edge-query-diagram', toQuery(q)] as const,
  pulseDiagram: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'pulse-diagram', toQuery(q)] as const,
  originShieldDiagram: (serviceId: string, kind: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'origin-shield', kind, toQuery(q)] as const,
  multiCdnDiagram: (serviceId: string, kind: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'multi-cdn', kind, toQuery(q)] as const,
  multiCdnUnsupported: (serviceId: string, kind: string) =>
    ['waf', serviceId, 'multi-cdn-unsupported', kind] as const,
  threatSummary: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'threat-summary', toQuery(q)] as const,
  threatTop: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'threat-top', toQuery(q)] as const,
  threatReportSummary: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'threat-report-summary', toQuery(q)] as const,
  threatIncidents: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'threat-incidents', toQuery(q)] as const,
  routingProfiles: (serviceId: string) => ['waf', serviceId, 'routing-profiles'] as const,
  routingStrategies: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'routing-strategies', domain] as const,
  routingZoneConfig: (serviceId: string, domain: string) =>
    ['waf', serviceId, 'routing-zone-config', domain] as const,
  routingReport: (serviceId: string, domain: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'routing-report', domain, toQuery(q)] as const,
  monitors: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'monitors', toQuery(q)] as const,
  home: (serviceId: string, kind: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'home', kind, toQuery(q)] as const,
  featureUsage: (serviceId: string, q?: Record<string, unknown>) =>
    ['waf', serviceId, 'feature-usage', toQuery(q)] as const,
  credit: (serviceId: string) => ['waf', serviceId, 'credit'] as const,
} as const;

// ---- Formatters ----------------------------------------------------------

/** Format a unix epoch (seconds or millis, BE sends int64 seconds) as a local
 *  date-time string; `—` when absent. */
export function fmtEpoch(v: number | null | undefined): string {
  if (!v) return '—';
  const ms = v < 1e12 ? v * 1000 : v;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
